import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

function WatchlistsPage({ onSelectWatchlist }) {
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchWatchlists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/api/watchlists");
      if (response.data.success) {
        setWatchlists(response.data.data);
      } else {
        setError(response.data.error || "Failed to load watchlists");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load watchlists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const response = await axios.post("/api/watchlists", { name });
      if (response.data.success) {
        setNewName("");
        fetchWatchlists();
      } else {
        setError(response.data.error || "Failed to create watchlist");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create watchlist");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this watchlist?")) return;
    try {
      const response = await axios.delete(`/api/watchlists/${id}`);
      if (response.data.success) {
        fetchWatchlists();
      } else {
        setError(response.data.error || "Failed to delete watchlist");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete watchlist");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" color="primary">
          Watchlists
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, backgroundColor: "background.paper", border: "1px solid", borderColor: "divider" }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="New watchlist name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            sx={{
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: "background.paper" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : watchlists.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="textSecondary">
              No watchlists yet. Create one above to get started.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Tickers</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Schedule</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Created</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }}>Updated</TableCell>
                <TableCell sx={{ color: "primary.main", fontWeight: "bold" }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {watchlists.map((wl) => (
                <TableRow
                  key={wl.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 255, 0, 0.05)",
                    },
                  }}
                  onClick={() => onSelectWatchlist(wl.id)}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: "bold", color: "secondary.main" }}>
                      {wl.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${wl.item_count || 0} ticker${(wl.item_count || 0) !== 1 ? "s" : ""}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={wl.schedule && wl.schedule !== 'none' ? wl.schedule.charAt(0).toUpperCase() + wl.schedule.slice(1) : 'None'}
                      size="small"
                      variant={wl.schedule && wl.schedule !== 'none' ? 'filled' : 'outlined'}
                      color={wl.schedule && wl.schedule !== 'none' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(wl.created_at)}</TableCell>
                  <TableCell>{formatDate(wl.updated_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(wl.id, e)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}

export default WatchlistsPage;
